import { Button, DatePicker, Modal, Spin, message, Icon } from 'antd'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import moment from 'moment'
import { ErrorContext, SpinContext } from 'context'
import {
  gettGenerationHourlyDOM,
  getGenerationLockCheck,
  saveGenerationHourlyDOM,
  deleteGenerationHourlyDOM,
  getHeInDay,
} from 'api/pgads'
import { FullHeightSizer } from 'core/screen-util'
import {
  freeEditEngine,
  Grid,
  virtualizedGridRenderer,
} from '@integec/grid-tools'
import R from 'ramda'
import { rowHeaderRender, hasAccesPermits } from 'core/utils'
import { numCol, strCol } from '@integec/grid-tools/lib/cols'
import * as ScreenPermissions from 'core/EventActions'
import { connect } from 'react-redux'
import {
  getApiTargetInfo,
  getClientInfo,
  getUnitNameMap,
  isAdminType,
  getUtilityNameMap,
  getPermitActions,
  getEnvConfig,
} from 'core'

const headers = [
  strCol({
    ident: `hour`,
    display: 'Hour',
    alignment: 'left',
    width: 100,
    ellipsis: true,
  }),
  numCol({
    ident: `gross`,
    display: 'Gross Generation',
    alignment: 'right',
    width: 120,
    ellipsis: true,
  }),
  numCol({
    ident: `net`,
    display: 'Net Generation',
    alignment: 'right',
    width: 120,
    ellipsis: true,
  }),
  numCol({
    ident: `ss`,
    display: 'Station Service',
    alignment: 'right',
    width: 120,
    ellipsis: true,
  }),
  numCol({
    ident: `pump`,
    display: 'Pump MW',
    alignment: 'right',
    width: 120,
    ellipsis: true,
  }),
  numCol({
    ident: `sync`,
    display: 'Sync MW',
    alignment: 'right',
    width: 120,
    ellipsis: true,
  }),
]

const convertGenerationData = (data = {}, is25Hour = false) => {
  const nd = R.range(1, 25).map(i => ({
    hour: i,
    gross: data['gross' + i],
    net: data['net' + i],
    ss: data['ss' + i],
    pump: data['pump' + i],
    sync: data['sync' + i],
  }))

  if (!is25Hour) return nd
  else
    return [
      ...nd,
      {
        hour: 25,
        gross: data.gross2a,
        net: data.net2a,
        ss: data.ss2a,
        pump: data.pump2a,
        sync: data.sync2a,
      },
    ]

  // return nd
}

const HourlyGenerationDetailsDOM = ({
  data,
  apitargetInfo,
  utilityNameMap,
  unitNameMap,
  insertEvent,
  setInsertEvent,
  isAdminType,
  onMainRetrieveData = _ => _,
  ...props
}) => {
  const { unitId, transdate, utility, unit, isNew = false } = data

  const { setError } = useContext(ErrorContext)
  const { setSpinning, spinning } = useContext(SpinContext)

  const [date, setDate] = useState(moment(transdate, 'YYYY-MM-DD'))
  const [lockStatus, setLockStatus] = useState('U')
  const [hours25, setHours25] = useState(false)

  const [generationData, setGenerationData] = useState(
    convertGenerationData(data, false)
  )

  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )

  const writePermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: props.envConfig.authGodMode,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.ADMIN_GENERATION_BY_HOUR_DOM],
        matchAny: true,
      }),
    [props.actionPermits, props.envConfig.authGodMode]
  )

  const onRetrieveData = useCallback(
    _ => {
      const req = {
        startdate: date.format('YYYY-MM-DD'),
        enddate: date.format('YYYY-MM-DD'),
        unitid: unitId,
      }

      setSpinning(true)

      gettGenerationHourlyDOM(apitargetInfo)(req).fork(setError, data => {
        setSpinning(false)
        setGenerationData(convertGenerationData(data[0], hours25))
      })
    },
    [apitargetInfo, date, setError, setSpinning, unitId, hours25]
  )

  const lockCheck = useCallback(
    _ => {
      if (isAdminType) {
        setLockStatus('U')
        return
      }
      const req = {
        utility,
        unit,
        transdate: date.format('YYYY-MM-DD'),
      }
      getGenerationLockCheck(apitargetInfo)(req).fork(
        setError,
        (status = ['U']) => {
          setLockStatus(isAdminType ? 'U' : status[0] || 'U')
        }
      )
    },
    [apitargetInfo, date, isAdminType, setError, unit, utility]
  )

  const getHoursList = useCallback(
    _ => {
      getHeInDay(apitargetInfo)(date).fork(setError, hours => {
        setHours25(hours.length === 25)
      })
    },
    [apitargetInfo, date, setError]
  )

  useEffect(
    _ => {
      lockCheck()
      getHoursList()
      onRetrieveData()
    },
    [date, onRetrieveData, lockCheck, getHoursList]
  )

  const incrementDate = _ => {
    setDate(date.clone().add(1, 'days'))
  }
  const decrymentDate = _ => {
    setDate(date.clone().subtract(1, 'days'))
  }

  const isEditable = useCallback(
    _ => {
      if (isAdminType) return true
      if (lockStatus === 'L') return false
      if (!writePermission) return false
      return true
    },
    [writePermission, isAdminType, lockStatus]
  )

  const isEditableGrid = useCallback(
    ({ header }) => {
      // if (!writePermission) return false
      if (header.ident === 'hour') return false
      return isEditable()
    },
    [isEditable]
  )

  const translateGenerationDataUiToApi = (updatedData, commonData) => {
    return updatedData.reduce((acc, item) => {
      const hour = item.hour
      return {
        ...acc,
        ['gross' + hour]: item.gross,
        ['net' + hour]: item.net,
        ['ss' + hour]: item.ss,
        ['pump' + hour]: item.pump,
        ['sync' + hour]: item.sync,
      }
    }, commonData)
  }

  const onSave = _ => {
    if (!isEditable()) {
      setInsertEvent(false)
      return
    }
    const updatedData = freeEditEngine.applyEdits({
      data: generationData,
      editInfo,
    })

    const commonData = {
      transdate: date.format('YYYY-MM-DD'),
      unit,
      utility,
    }
    let translatedData = translateGenerationDataUiToApi(updatedData, commonData)

    translatedData = {
      ...translatedData,
      gross2a: translatedData.gross25,
      net2a: translatedData.net25,
      ss2a: translatedData.ss25,
      sync2a: translatedData.sync25,
      pump2a: translatedData.pump25,
    }

    setSpinning(true)
    saveGenerationHourlyDOM(apitargetInfo)([translatedData]).fork(
      setError,
      _ => {
        setSpinning(false)
        setInsertEvent(false)
        onMainRetrieveData()
      }
    )
  }

  const onDelete = _ => {
    if (!isEditable()) {
      setInsertEvent(false)
      return
    }

    Modal.confirm({
      content:
        'Are you sure you want to delete Genearation data for ' +
        unitNameMap[unitId] +
        '(' +
        utility +
        '-' +
        unit +
        ') Date : ' +
        moment(date).format('MM/DD/YYYY') +
        ' ?',
      title: 'Delete Generation',
      onOk() {
        setSpinning(true)
        const delData = [
          { unit, utility, transdate: date.format('YYYY-MM-DD') },
        ]
        deleteGenerationHourlyDOM(apitargetInfo)(delData).fork(setError, _ => {
          setSpinning(false)
          message.info('Data Saved')
          setInsertEvent(false)
          onMainRetrieveData()
        })
      },
      onCancel() {},
    })
  }

  return (
    <Modal
      destroyOnClose
      visible={insertEvent}
      onCancel={_ => setInsertEvent(false)}
      onOk={onSave}
      width="900px"
      okText="Save"
      style={{ top: 0, height: '100%' }}
      bodyStyle={{ height: '100%' }}
      title={
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              '20px max-content max-content max-content max-content',
            // gridColumnGap: '15px',
            justifyContent: 'space-evenly',
          }}
        >
          <div>
            {lockStatus === 'L' && !isAdminType && (
              <Icon
                type={lockStatus === 'L' && !isAdminType ? 'lock' : 'unlock'}
                style={{
                  position: 'absolute',
                  fontSize: '30px',
                }}
                theme="twoTone"
              />
            )}
          </div>
          <div>Generation Data</div>
          <div>
            {utility} {utilityNameMap[utility]}
          </div>
          <div>{`${unit + ' ' + unitNameMap[unitId]}`}</div>
          <div>Date :{date.format('MM/DD/YYYY')}</div>
        </div>
      }
      footer={
        <div>
          <Button onClick={_ => setInsertEvent(false)}>Cancel</Button>
          {isEditable() && (
            <Button onClick={onSave} type="primary" icon="edit">
              Save
            </Button>
          )}
        </div>
      }
      maskClosable={false}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'min-content auto',
          gridRowGap: '15px',
        }}
      >
        <div
          style={{
            display: 'grid',
            justifyContent: 'center',
            gridTemplateColumns: 'auto auto auto auto',
            gridColumnGap: '20px',
          }}
        >
          <Button onClick={decrymentDate} type="primary">
            Previous Day
          </Button>
          <DatePicker
            value={date}
            onChange={setDate}
            format="MM/DD/YYYY"
            allowClear={false}
          />
          <Button onClick={incrementDate} type="primary">
            Next Day
          </Button>
          {isEditable() && !isNew && (
            <Button
              type="danger"
              style={{ position: 'fixed', justifySelf: 'right' }}
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </div>
        <Spin spinning={spinning}>
          <FullHeightSizer heightOffset={250}>
            {({ height, width }) => (
              <div>
                <Grid
                  data={generationData}
                  headers={headers}
                  render={virtualizedGridRenderer({
                    headerRowHeight: 30,
                    autoFixColByKey: false,
                    rowHeaderRender,
                    height: height - 20,
                    width: width - 50,
                    isEditable: true,
                    // cellRender: customizedCellRender,
                  })}
                  isEditable={isEditableGrid}
                  // selectionMode="single"
                  selectionType="cell"
                  // initialSortOptions={headers}
                  editMode="cell"
                  onEditInfoChange={setEditInfo}
                  editInfo={editInfo}
                />
              </div>
            )}
          </FullHeightSizer>
        </Spin>
      </div>
    </Modal>
  )
}

const mapStateToProps = state => ({
  CLIENT: getClientInfo(state),
  isAdminType: isAdminType(state),
  apitargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
})

export default connect(mapStateToProps)(HourlyGenerationDetailsDOM)
// export default HourlyGenerationDetailsDOM
